'use client'

import { useMemo } from 'react'

import { sumBy } from 'lodash'

import { buildTokenFilterKey } from '~/app/accounting/filters/helpers'
import { Period } from '~/app/accounting/period-utils'
import {
  getAllChains,
  hasCapability,
  SupportedChainId,
} from '~/constants/chains'
import { useActiveOrg } from '~/hooks/org'
import { useAccountingTimezone } from '~/hooks/useAccountingTimezone'
import { RawOrgBalancesResponse, useOrgBalances } from '~/hooks/useBalances'
import { useHistoricalBalances } from '~/hooks/useHistoricalBalances'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { SmartAccountRole } from '~/types'
import { TokenBalance } from '~/utils/account'
import { EvmAddress } from '~/utils/address'
import { AccountingTz, sameDayInTz, startOfMonth } from '~/utils/timezone'

export type FlatToken = {
  address: EvmAddress.Type
  chainId: SupportedChainId
  symbol: string
  rawBalance: string
  usdValue: number | undefined
}

// Accounting only reports networks it can account for. An unset Network
// filter means "every accounting network", not "every network the org holds
// a balance on" — otherwise a balance-only chain lands in the summary and the
// Tokens tab while Transactions, which is already scoped this way, shows
// nothing for it, and the totals contradict the history. An explicit filter is
// already drawn from the same capability, so it passes through untouched.
export const getAccountingChainIds = (chainIds?: number[]): number[] =>
  chainIds && chainIds.length > 0
    ? chainIds
    : getAllChains()
        .filter((chain) => hasCapability(chain.chainId, 'accounting'))
        .map((chain) => chain.chainId)

export const getFilteredOrgBalances = ({
  realtimeOrgBalances,
  allowedAddresses,
  chainIds,
}: {
  realtimeOrgBalances: {
    [address: string]: { balances: TokenBalance[]; totalValue: number }
  }
  allowedAddresses: Set<string> | null
  chainIds: number[] | undefined
}): RawOrgBalancesResponse => {
  const chainIdsSet = chainIds && chainIds.length > 0 ? new Set(chainIds) : null
  const filteredBalances = Object.fromEntries(
    Object.entries(realtimeOrgBalances).filter(([address]) =>
      allowedAddresses ? allowedAddresses.has(address.toLowerCase()) : true,
    ),
  )

  const balances: RawOrgBalancesResponse['balances'] = Object.fromEntries(
    Object.entries(filteredBalances).map(([address, accountBalance]) => {
      const accountBalances = accountBalance.balances.filter((balance) =>
        chainIdsSet ? chainIdsSet.has(balance.chainId) : true,
      )
      const totalValue = accountBalances.reduce(
        (sum, balance) => sum + (balance.usdValue ?? 0),
        0,
      )

      return [
        address,
        {
          balances: accountBalances.map((balance) => ({
            ...balance,
            amount: balance.amount.toString(),
          })),
          totalValue,
        },
      ]
    }),
  )

  return {
    balances,
    totalValue: Object.values(balances).reduce(
      (sum, accountBalance) => sum + accountBalance.totalValue,
      0,
    ),
  }
}

const getPeriodDates = (
  period: Period | null,
  tz: AccountingTz,
): { startDate: Date | undefined; endDate: Date; endDateIsNow: boolean } => {
  const startTime = period?.startDate?.getTime()
  const endTime = period?.endDate?.getTime()

  // A selected period runs from its start (or the beginning of time when
  // unbounded, e.g. "All time") up to its end (or now). A missing startDate
  // leaves the start query disabled, so the starting balance reads as "-".
  if (period) {
    const endDate = endTime ? new Date(endTime) : new Date()
    return {
      startDate: startTime ? new Date(startTime) : undefined,
      endDate,
      endDateIsNow: !endTime || sameDayInTz(endDate, new Date(), tz),
    }
  }

  // No period selected at all → default to the current month.
  const now = new Date()
  return {
    startDate: startOfMonth(now, tz),
    endDate: now,
    endDateIsNow: true,
  }
}

export const getHistoricalEndDate = ({
  endDate,
  endDateIsNow,
  endUsesRealtime,
  now = new Date(),
}: {
  endDate: Date
  endDateIsNow: boolean
  endUsesRealtime: boolean
  now?: Date
}) => (endDateIsNow && !endUsesRealtime ? now : endDate)

const useEndRealtimeBalances = ({
  endDateIsNow,
  accountIds,
  chainIds,
  orgId,
}: {
  endDateIsNow: boolean
  accountIds?: string[]
  chainIds?: number[]
  orgId?: string
}) => {
  const {
    balances: realtimeOrgBalances,
    isLoading: isRealtimeBalancesLoading,
    isError: isRealtimeBalancesError,
  } = useOrgBalances(endDateIsNow ? orgId : undefined)
  // Include archived and automation accounts so their balances aren't dropped.
  const { smartAccounts, isLoading: isSmartAccountsLoading } = useSmartAccounts(
    { showArchived: true, includeAutomations: true },
  )

  const data = useMemo<RawOrgBalancesResponse | null>(() => {
    if (!endDateIsNow) return null

    const accountIdsSet =
      accountIds && accountIds.length > 0 ? new Set(accountIds) : null
    const allowedAddresses = accountIdsSet
      ? new Set(
          smartAccounts
            .filter((account) => accountIdsSet.has(account.id))
            .map((account) => account.address.toLowerCase()),
        )
      : null

    return getFilteredOrgBalances({
      realtimeOrgBalances,
      allowedAddresses,
      chainIds,
    })
  }, [accountIds, chainIds, endDateIsNow, realtimeOrgBalances, smartAccounts])

  const isLoading =
    isRealtimeBalancesLoading ||
    ((accountIds?.length ?? 0) > 0 && isSmartAccountsLoading)

  return { data, isLoading, isError: isRealtimeBalancesError }
}

// Flatten org balances into deduped FlatToken[] (summed across accounts),
// dropping zero balances and sorting by USD value desc.
export const getTokensSortedByValue = (
  data: RawOrgBalancesResponse | null | undefined,
): FlatToken[] => {
  if (!data) return []

  const tokens = Object.values(data.balances).flatMap((account) =>
    account.balances
      .filter((t) => t.amount !== '0')
      .map((t) => ({
        address: t.address as EvmAddress.Type,
        chainId: t.chainId as SupportedChainId,
        symbol: t.tokenMetadata?.symbol ?? '',
        rawBalance: t.amount as string,
        usdValue: t.usdValue,
      })),
  )

  const map = new Map<string, FlatToken>()
  tokens.forEach((token) => {
    const key = `${token.chainId}:${token.address}`
    const existing = map.get(key)
    if (existing) {
      existing.rawBalance = (
        BigInt(existing.rawBalance) + BigInt(token.rawBalance)
      ).toString()
      existing.usdValue = (existing.usdValue ?? 0) + (token.usdValue ?? 0)
    } else {
      map.set(key, { ...token })
    }
  })

  return Array.from(map.values()).sort(
    (a, b) => (b.usdValue ?? 0) - (a.usdValue ?? 0),
  )
}

// USD total of the tokens matching the given filter keys (unpriced rows count
// as 0, mirroring how the unfiltered totals treat them).
export const sumTokenValues = (
  tokens: FlatToken[],
  tokenKeys: Set<string>,
): number =>
  sumBy(
    tokens.filter((token) =>
      tokenKeys.has(
        buildTokenFilterKey({
          chainId: token.chainId,
          address: token.address,
        }),
      ),
    ),
    (token) => token.usdValue ?? 0,
  )

type AccountingTokenPositions = {
  startTokens: FlatToken[]
  endTokens: FlatToken[]
  startTotal: number | undefined
  endTotal: number | undefined
  netChange: number | undefined
  startDate: Date | undefined
  endDate: Date
  endDateIsNow: boolean
  startLoading: boolean
  endLoading: boolean
  hasError: boolean
  tz: AccountingTz
}

// Token positions at a period's start/end as sorted FlatToken[] lists
// (end uses realtime balances when the period ends now). tokenKeys scopes the
// totals (not the token lists) to the matching tokens.
export const useAccountingTokenPositions = ({
  period,
  accountIds,
  chainIds,
  tokenKeys,
}: {
  period: Period | null
  accountIds?: string[]
  chainIds?: number[]
  tokenKeys?: string[]
}): AccountingTokenPositions => {
  const { org } = useActiveOrg()
  const [tz] = useAccountingTimezone()

  // Resolved once so the two historical reads and the realtime filter below
  // all describe the same set of networks.
  const accountingChainIds = useMemo(
    () => getAccountingChainIds(chainIds),
    [chainIds],
  )

  const startTime = period?.startDate?.getTime()
  const endTime = period?.endDate?.getTime()
  const { startDate, endDate, endDateIsNow } = useMemo(
    () => getPeriodDates(period, tz),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startTime, endTime, tz],
  )

  // Realtime org balances only cover active accounts, so a filter containing
  // archived or automation accounts reads the period end from the historical
  // endpoint even when the period ends now.
  const { smartAccounts } = useSmartAccounts({
    showArchived: true,
    includeAutomations: true,
  })
  const hasInactiveAccountFilter = useMemo(() => {
    if (!accountIds || accountIds.length === 0) return false
    return smartAccounts.some(
      (account) =>
        (account.isArchived || account.role === SmartAccountRole.AUTOMATION) &&
        accountIds.includes(account.id),
    )
  }, [accountIds, smartAccounts])
  const endUsesRealtime = endDateIsNow && !hasInactiveAccountFilter

  // Calendar periods end at 11:59:59 PM. Clamp today's historical fallback to
  // the current instant so the server doesn't cache a latest-block response
  // under a future timestamp.
  const historicalEndDate = useMemo(
    () =>
      getHistoricalEndDate({
        endDate,
        endDateIsNow,
        endUsesRealtime,
      }),
    [endDate, endDateIsNow, endUsesRealtime],
  )

  const startQuery = useHistoricalBalances({
    orgId: org?.id,
    date: startDate?.toISOString(),
    accountIds,
    chainIds: accountingChainIds,
    enabled: !!org?.id && !!startDate,
  })

  const endHistoricalQuery = useHistoricalBalances({
    orgId: org?.id,
    date: historicalEndDate.toISOString(),
    accountIds,
    chainIds: accountingChainIds,
    enabled: !!org?.id && !endUsesRealtime,
  })

  const {
    data: endRealtimeData,
    isLoading: isRealtimeLoading,
    isError: isRealtimeError,
  } = useEndRealtimeBalances({
    endDateIsNow: endUsesRealtime,
    accountIds,
    chainIds: accountingChainIds,
    orgId: org?.id,
  })

  const startLoading = startQuery.isLoading || startQuery.isFetching
  const endLoading = endUsesRealtime
    ? isRealtimeLoading
    : endHistoricalQuery.isLoading || endHistoricalQuery.isFetching
  // The end error must follow the same branch as the end data (else a failed
  // realtime fetch leaves endTokens empty and every token reads as a 100% outflow).
  const hasError =
    startQuery.isError ||
    (endUsesRealtime ? isRealtimeError : endHistoricalQuery.isError)

  const startTokens = useMemo(
    () => getTokensSortedByValue(startQuery.data),
    [startQuery.data],
  )
  const endTokens = useMemo(
    () =>
      endUsesRealtime
        ? getTokensSortedByValue(endRealtimeData)
        : getTokensSortedByValue(endHistoricalQuery.data),
    [endUsesRealtime, endHistoricalQuery.data, endRealtimeData],
  )

  const unfilteredStartTotal = startLoading
    ? undefined
    : startQuery.data?.totalValue
  const unfilteredEndTotal = endLoading
    ? undefined
    : endUsesRealtime
      ? endRealtimeData?.totalValue
      : endHistoricalQuery.data?.totalValue

  // Token-filtered totals sum the flattened rows (both balance responses carry
  // per-token entries), gated on the unfiltered total so absent data stays "-"
  // instead of reading as $0.
  const tokenKeySet =
    tokenKeys && tokenKeys.length > 0 ? new Set(tokenKeys) : null
  const startTotal =
    tokenKeySet && unfilteredStartTotal != null
      ? sumTokenValues(startTokens, tokenKeySet)
      : unfilteredStartTotal
  const endTotal =
    tokenKeySet && unfilteredEndTotal != null
      ? sumTokenValues(endTokens, tokenKeySet)
      : unfilteredEndTotal
  const netChange =
    startTotal != null && endTotal != null ? endTotal - startTotal : undefined

  return {
    startTokens,
    endTokens,
    startTotal,
    endTotal,
    netChange,
    startDate,
    endDate,
    endDateIsNow,
    startLoading,
    endLoading,
    hasError,
    tz,
  }
}
